import { openModal } from 'flavours/glitch/actions/modal';
import { browserHistory } from 'flavours/glitch/components/router';
import { layoutFromWindow } from 'flavours/glitch/is_mobile';
import { normalizeInlineComposePath } from 'flavours/glitch/features/ui/components/inline_compose_routes';

const detailedStatusPath = /^\/(?:@[^/]+\/[^/]+|statuses\/[^/]+)\/?$/;
const inlineComposeFeedPath = /^\/(?:home|timelines\/home|public(?:\/local)?|timelines\/public(?:\/local)?|lists\/[^/]+|antennas\/[^/]+|timelines\/antenna\/[^/]+|conversations|timelines\/direct)\/?$/;
const mobileReplyBreakpoint = 759;

export const isMobileReplyViewport = width => width <= mobileReplyBreakpoint;

export const isStandaloneComposePath = pathname => [
  '/publish',
  '/statuses/new',
].includes(normalizeInlineComposePath(pathname));

export const shouldReturnFromComposePage = ({
  fromMastodon,
  pathname,
  threadedMode,
}) => fromMastodon && !threadedMode && isStandaloneComposePath(pathname);

const isSingleColumnInlineComposeRoute = ({ enabled, layout, pathname }) =>
  enabled && layout !== 'multi-column' && (
    detailedStatusPath.test(normalizeInlineComposePath(pathname)) || inlineComposeFeedPath.test(normalizeInlineComposePath(pathname))
  );

export const shouldExitDetailedForInlineCompose = ({
  enabled,
  layout,
  pathname,
}) => enabled && layout !== 'multi-column' && detailedStatusPath.test(normalizeInlineComposePath(pathname));

export const shouldOpenInlineComposeReplyModal = ({
  disablePopup,
  disableMobileComposeModal,
  layout,
  mobile,
  ...options
}) => isSingleColumnInlineComposeRoute({ ...options, layout }) && !(
  mobile ? disableMobileComposeModal : disablePopup
);

export const shouldNavigateToComposePage = ({
  disableMobileComposeModal,
  layout,
  mobile,
  ...options
}) => disableMobileComposeModal && mobile && isSingleColumnInlineComposeRoute({
  ...options,
  layout,
});

export const shouldOpenInlineComposeDirectModal = ({
  disableMobileComposeModal = false,
  enabled,
  layout,
  mobile = false,
}) => enabled && layout !== 'multi-column' && !(
  disableMobileComposeModal && mobile
);

const scrollToTopAfterNavigation = () => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }
    });
  });
};

export const handleReplyForInlineCompose = (dispatch, getState) => {
  const options = {
    disablePopup: getState().getIn(['local_settings', 'disable_inline_compose_reply_modal'], false),
    disableMobileComposeModal: getState().getIn(['local_settings', 'disable_mobile_compose_modal'], true),
    enabled: getState().getIn(['local_settings', 'inline_compose_timelines'], false),
    layout: layoutFromWindow(),
    mobile: isMobileReplyViewport(window.innerWidth),
    pathname: browserHistory.location.pathname,
  };

  if (shouldNavigateToComposePage(options)) {
    browserHistory.push('/publish', { focusTarget: false });
    return true;
  }

  if (shouldOpenInlineComposeReplyModal(options)) {
    dispatch(openModal({
      modalType: 'INLINE_COMPOSE',
      modalProps: {},
    }));
    return true;
  }

  if (!shouldExitDetailedForInlineCompose(options)) {
    return false;
  }

  const unlisten = browserHistory.listen(() => {
    unlisten();
    scrollToTopAfterNavigation();
  });

  if (browserHistory.location.state?.fromMastodon) {
    browserHistory.goBack();
  } else {
    browserHistory.push('/', { focusTarget: false });
  }

  return true;
};

export const handleDirectForInlineCompose = (dispatch, getState, options = {}) => {
  const { force = false, navigateToConversation = false } = options;
  const composeOptions = {
    disableMobileComposeModal: getState().getIn(['local_settings', 'disable_mobile_compose_modal'], true),
    enabled: getState().getIn(['local_settings', 'inline_compose_timelines'], false),
    layout: layoutFromWindow(),
    mobile: isMobileReplyViewport(window.innerWidth),
  };

  if (!force && !shouldOpenInlineComposeDirectModal(composeOptions)) {
    return false;
  }

  dispatch(openModal({
    modalType: 'INLINE_COMPOSE',
    modalProps: { title: 'direct', navigateToConversation },
  }));
  return true;
};
